import React, { useState, useEffect } from "react";
import axios from "axios";
import { 
  Sparkles, 
  TrendingUp, 
  TrendingDown, 
  Minus,
  AlertTriangle, 
  CheckCircle, 
  XCircle,
  Plane,
  Wind,
  Eye,
  Loader2,
  MapPin,
  Thermometer,
  Droplets,
  RefreshCw
} from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GoNoGoBadge = ({ recommendation }) => {
  const config = {
    GO: { bg: "bg-emerald-500", text: "text-white", label: "GO" },
    GO_WITH_CAUTION: { bg: "bg-amber-500", text: "text-white", label: "CAUTION" },
    NO_GO: { bg: "bg-red-500", text: "text-white", label: "NO GO" }
  };
  const c = config[recommendation] || config.GO_WITH_CAUTION;
  
  return (
    <span className={`px-3 py-1 rounded-full text-xs font-bold ${c.bg} ${c.text}`}>
      {c.label}
    </span>
  );
};

const ConditionBadge = ({ condition }) => {
  const colors = {
    VFR: "bg-emerald-500",
    MVFR: "bg-blue-500",
    IFR: "bg-red-500",
    LIFR: "bg-purple-500"
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-bold text-white ${colors[condition] || "bg-slate-500"}`}>
      {condition}
    </span>
  );
};

const RiskDot = ({ level }) => {
  const colors = {
    LOW: "bg-emerald-500",
    MODERATE: "bg-amber-500",
    HIGH: "bg-orange-500",
    SEVERE: "bg-red-500"
  };
  return <span className={`w-2 h-2 rounded-full ${colors[level] || "bg-slate-500"}`}></span>;
};

export default function AIWeatherPrediction({ icao, metar, taf, ftxDesignator, onRouteAirportsFound }) {
  const [prediction, setPrediction] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [routeAirports, setRouteAirports] = useState(null);
  const [satelliteData, setSatelliteData] = useState(null);

  // Auto-analyze when METAR changes
  useEffect(() => {
    if (metar && icao) {
      fetchPrediction();
    }
  }, [metar, icao]);

  const fetchPrediction = async () => {
    if (!icao && !metar) return;

    setLoading(true);
    setError(null);

    try {
      // Get route airports if FTX designator is set
      let routeIcaos = [];
      if (ftxDesignator) {
        try {
          const routeResp = await axios.post(`${API}/weather/route-airports`, {
            ftx_designator: ftxDesignator,
            radius_nm: 60
          });
          setRouteAirports(routeResp.data);
          routeIcaos = [
            ...routeResp.data.route_airports.map(a => a.icao),
            ...routeResp.data.alternates.slice(0, 5).map(a => a.icao)
          ];
          if (onRouteAirportsFound) {
            onRouteAirportsFound(routeResp.data);
          }
        } catch (e) {
          console.error("Route airports error:", e);
        }
      }

      // Get satellite data
      try {
        const satResp = await axios.get(`${API}/weather/openweather/${icao}`);
        if (!satResp.data.current?.error) {
          setSatelliteData(satResp.data);
        }
      } catch (e) {
        console.log("Satellite data unavailable");
      }

      // Get AI prediction
      const response = await axios.post(`${API}/weather/ai-prediction`, {
        icao: icao,
        metar: metar,
        taf: taf,
        route_airports: routeIcaos
      });

      setPrediction(response.data);
    } catch (err) {
      console.error("AI prediction error:", err);
      setError(err.response?.data?.detail || "Analysis failed");
    } finally {
      setLoading(false);
    }
  };

  const pred = prediction?.prediction;

  if (!metar && !icao) {
    return null;
  }

  return (
    <div className="space-y-3">
      {/* Compact Header with GO/NO GO */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-orange-500 dark:text-orange-400" />
          <span className="text-sm font-semibold text-orange-600 dark:text-orange-400">AI ANALYSIS</span>
          {pred?.go_no_go && <GoNoGoBadge recommendation={pred.go_no_go.recommendation} />}
          {pred?.risk_assessment && <RiskDot level={pred.risk_assessment.overall_risk} />}
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={fetchPrediction}
          disabled={loading}
          className="h-7 px-2 text-orange-600 hover:text-orange-500 hover:bg-orange-500/10 dark:text-orange-400 dark:hover:text-orange-300"
        >
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
        </Button>
      </div>

      {loading && (
        <div className="flex items-center gap-2 py-2 text-sm text-slate-600 dark:text-slate-400">
          <Loader2 className="w-4 h-4 animate-spin text-orange-500 dark:text-orange-400" />
          Analyzing weather data...
        </div>
      )}

      {error && (
        <div className="text-xs text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-500/10 px-2 py-1 rounded">{error}</div>
      )}

      {pred && !loading && (
        <div className="space-y-3">
          {/* Decision Summary - Most Important */}
          {pred.go_no_go && (
            <div className="p-3 rounded-lg bg-slate-100 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700">
              <p className="text-sm text-slate-800 dark:text-slate-200 leading-relaxed">{pred.go_no_go.summary}</p>
              {pred.go_no_go.primary_concerns?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {pred.go_no_go.primary_concerns.slice(0, 3).map((c, i) => (
                    <span key={i} className="text-xs px-2 py-0.5 rounded bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />{c}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Quick Stats Row */}
          <div className="grid grid-cols-4 gap-2">
            {/* Trend */}
            <div className="p-2 rounded bg-slate-100 dark:bg-slate-800/50 text-center">
              <div className="flex items-center justify-center gap-1 mb-1">
                {pred.trend_analysis?.current_trend === "improving" && <TrendingUp className="w-3 h-3 text-emerald-500 dark:text-emerald-400" />}
                {pred.trend_analysis?.current_trend === "deteriorating" && <TrendingDown className="w-3 h-3 text-red-500 dark:text-red-400" />}
                {pred.trend_analysis?.current_trend === "stable" && <Minus className="w-3 h-3 text-amber-500 dark:text-amber-400" />}
              </div>
              <span className="text-xs text-slate-600 dark:text-slate-400 capitalize">{pred.trend_analysis?.current_trend || "N/A"}</span>
            </div>

            {/* Expected Conditions */}
            <div className="p-2 rounded bg-slate-100 dark:bg-slate-800/50 text-center">
              {pred.arrival_prediction?.expected_conditions && (
                <ConditionBadge condition={pred.arrival_prediction.expected_conditions} />
              )}
              <div className="text-xs text-slate-600 dark:text-slate-400 mt-1">Arrival</div>
            </div>

            {/* Visibility */}
            <div className="p-2 rounded bg-slate-100 dark:bg-slate-800/50 text-center">
              <div className="flex items-center justify-center gap-1">
                <Eye className="w-3 h-3 text-slate-400 dark:text-slate-500" />
                <span className="text-sm font-medium text-slate-900 dark:text-white">{pred.arrival_prediction?.visibility_km || "?"}</span>
                <span className="text-xs text-slate-500">km</span>
              </div>
              <div className="text-xs text-slate-600 dark:text-slate-400">Visibility</div>
            </div>

            {/* Wind */}
            <div className="p-2 rounded bg-slate-100 dark:bg-slate-800/50 text-center">
              <div className="flex items-center justify-center gap-1">
                <Wind className="w-3 h-3 text-slate-400 dark:text-slate-500" />
                <span className="text-sm font-medium text-slate-900 dark:text-white">{pred.arrival_prediction?.wind_kt || "?"}</span>
                <span className="text-xs text-slate-500">kt</span>
              </div>
              <div className="text-xs text-slate-600 dark:text-slate-400">Wind</div>
            </div>
          </div>

          {/* Satellite Data (if available) */}
          {satelliteData?.current && !satelliteData.current.error && (
            <div className="p-2 rounded bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/20">
              <div className="flex items-center gap-1 mb-1">
                <span className="text-xs text-blue-600 dark:text-blue-400 font-semibold">SATELLITE DATA</span>
              </div>
              <div className="grid grid-cols-4 gap-2 text-xs">
                <div className="flex items-center gap-1">
                  <Thermometer className="w-3 h-3 text-blue-500 dark:text-blue-400" />
                  <span className="text-slate-900 dark:text-white">{Math.round(satelliteData.current.temp)}°C</span>
                </div>
                <div className="flex items-center gap-1">
                  <Droplets className="w-3 h-3 text-blue-500 dark:text-blue-400" />
                  <span className="text-slate-900 dark:text-white">{satelliteData.current.humidity}%</span>
                </div>
                <div className="flex items-center gap-1">
                  <Wind className="w-3 h-3 text-blue-500 dark:text-blue-400" />
                  <span className="text-slate-900 dark:text-white">{Math.round(satelliteData.current.wind_speed * 1.944)}kt</span>
                </div>
                <div className="flex items-center gap-1">
                  <Eye className="w-3 h-3 text-blue-500 dark:text-blue-400" />
                  <span className="text-slate-900 dark:text-white">{satelliteData.current.visibility}km</span>
                </div>
              </div>
            </div>
          )}

          {/* Route Airports & Alternates */}
          {(pred.route_analysis?.suggested_alternates?.length > 0 || routeAirports) && (
            <div className="p-2 rounded bg-slate-100 dark:bg-slate-800/50">
              <div className="flex items-center gap-1 mb-2">
                <MapPin className="w-3 h-3 text-purple-500 dark:text-purple-400" />
                <span className="text-xs text-purple-600 dark:text-purple-400 font-semibold">ROUTE & ALTERNATES</span>
              </div>
              <div className="flex flex-wrap gap-1">
                {routeAirports?.route_airports?.slice(0, 4).map((apt, i) => (
                  <span key={i} className="text-xs px-2 py-0.5 rounded bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 font-mono">
                    {apt.icao}
                  </span>
                ))}
                {pred.route_analysis?.suggested_alternates?.map((alt, i) => (
                  <span key={`alt-${i}`} className="text-xs px-2 py-0.5 rounded bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 font-mono">
                    {alt} ✓
                  </span>
                ))}
              </div>
              {routeAirports?.alternates?.length > 0 && (
                <div className="mt-1 text-xs text-slate-500">
                  +{routeAirports.alternates.length} alternates within 60nm
                </div>
              )}
            </div>
          )}

          {/* Recommendations - Compact List */}
          {pred.risk_assessment?.recommendations?.length > 0 && (
            <div className="p-2 rounded bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20">
              <span className="text-xs text-amber-600 dark:text-amber-400 font-semibold">RECOMMENDATIONS</span>
              <ul className="mt-1 space-y-0.5">
                {pred.risk_assessment.recommendations.slice(0, 3).map((rec, i) => (
                  <li key={i} className="text-xs text-slate-700 dark:text-slate-300 flex items-start gap-1">
                    <CheckCircle className="w-3 h-3 text-amber-500 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                    {rec}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Initial prompt if no data */}
      {!pred && !loading && !error && metar && (
        <Button
          size="sm"
          onClick={fetchPrediction}
          className="w-full bg-orange-500 hover:bg-orange-600 text-white"
        >
          <Sparkles className="w-4 h-4 mr-2" />
          Analyze Weather
        </Button>
      )}
    </div>
  );
}
